import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from './ui/Button';
import { federFlaeche, transition, uebergangSanft } from '../lib/motion';

export interface BestaetigungProps {
  offen: boolean;
  titel: string;
  children?: ReactNode;
  /** Beschriftung der Hauptaktion, z. B. „Objekt löschen“. */
  aktion: string;
  /** gefahr = unwiderruflich (rot), primaer = umkehrbar (z. B. Archivieren). */
  ton?: 'gefahr' | 'primaer';
  /**
   * Erfordert die Eingabe dieses Textes vor der Ausführung (Schutz vor versehentlichem
   * Löschen). Vergleich ohne Groß-/Kleinschreibung und Leerzeichen am Rand.
   */
  eingabePflicht?: string;
  ladend?: boolean;
  /** Reiner Hinweis: nur eine Schaltfläche (die Aktion), kein „Abbrechen“. */
  nurHinweis?: boolean;
  /** Aktion nicht möglich (Grund steht in `fehler`). */
  gesperrt?: boolean;
  fehler?: string | null;
  onBestaetigen: () => void;
  onAbbrechen: () => void;
}

/**
 * Bestätigungsdialog für folgenreiche Aktionen. Auf kleinen Geräten als Blatt von unten,
 * ab Tablet zentriert. Schließt über Escape, Hintergrund oder „Abbrechen“ — nie während
 * die Aktion läuft.
 */
export function Bestaetigung({
  offen,
  titel,
  children,
  aktion,
  ton = 'gefahr',
  eingabePflicht,
  ladend = false,
  gesperrt = false,
  nurHinweis = false,
  fehler,
  onBestaetigen,
  onAbbrechen,
}: BestaetigungProps) {
  const reduziert = useReducedMotion();
  const titelId = useId();
  const eingabeId = useId();
  const [eingabe, setEingabe] = useState('');
  const dialog = useRef<HTMLDivElement | null>(null);
  const abbrechen = useRef(onAbbrechen);
  abbrechen.current = onAbbrechen;
  const laeuft = useRef(ladend);
  laeuft.current = ladend;

  useEffect(() => {
    if (!offen) return;
    setEingabe('');
    const t = window.setTimeout(
      () => dialog.current?.querySelector<HTMLElement>('input, [data-abbrechen], button[type=button]:not([tabindex])')?.focus(),
      30,
    );
    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape' && !laeuft.current) abbrechen.current();
    }
    window.addEventListener('keydown', beiTaste);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', beiTaste);
    };
  }, [offen]);

  const freigegeben =
    !eingabePflicht || eingabe.trim().toLocaleLowerCase('de') === eingabePflicht.trim().toLocaleLowerCase('de');

  // Portal an document.body: Vorfahren mit transform (Seitenübergänge) würden `fixed`
  // sonst auf ihren eigenen Rahmen beschränken.
  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      {offen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
          <motion.button
            type="button"
            aria-label="Dialog schließen"
            tabIndex={-1}
            className="absolute inset-0 h-full w-full cursor-default bg-fg/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transition(!!reduziert, uebergangSanft)}
            onClick={() => !ladend && onAbbrechen()}
          />
          <motion.div
            ref={dialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titelId}
            className="relative w-full max-w-lg rounded-t-2xl border border-border bg-surface p-5 shadow-lg sicherheitsabstand-unten sm:rounded-2xl"
            initial={reduziert ? { opacity: 0 } : { y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduziert ? { opacity: 0 } : { y: 40, opacity: 0 }}
            transition={transition(!!reduziert, federFlaeche)}
          >
            <div className="mx-auto -mt-1 mb-3 h-1 w-10 rounded-pill bg-border sm:hidden" aria-hidden="true" />
            <h2 id={titelId} className="text-lg font-semibold text-fg">
              {titel}
            </h2>
            {children && <div className="mt-2 space-y-2 text-sm leading-relaxed text-fg-muted">{children}</div>}

            {eingabePflicht && (
              <label htmlFor={eingabeId} className="mt-4 block">
                <span className="mb-1.5 block text-sm font-medium text-fg">
                  Zur Bestätigung bitte <span className="font-semibold">„{eingabePflicht}“</span> eingeben
                </span>
                <input
                  id={eingabeId}
                  value={eingabe}
                  onChange={(e) => setEingabe(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && freigegeben && !gesperrt && !ladend) onBestaetigen();
                  }}
                />
              </label>
            )}

            {fehler && (
              <p role="alert" className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
                {fehler}
              </p>
            )}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {!nurHinweis && (
                <Button variante="sekundaer" onClick={onAbbrechen} disabled={ladend} data-abbrechen>
                  Abbrechen
                </Button>
              )}
              <Button
                variante={ton === 'gefahr' ? 'gefahrStark' : 'primaer'}
                onClick={onBestaetigen}
                disabled={!freigegeben || gesperrt}
                ladend={ladend}
              >
                {aktion}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
